import { useBlockedIntervals } from "components/Dashboard/DoctorDashboard/views/one-one-sessions/contexts/BlockedIntervalsContext";
import ErrorTag from "components/error-tag";
import { DAYS_OF_WEEK } from "constants/daysOfWeek";
import moment from "moment-timezone";
import { useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useSwipeable } from "react-swipeable";
import { selectNewSessionPlaceholder } from "store/calendarSidebarSlice";
import { AmplitudePageName } from "utils/amplitude";
import { useSchedule } from "../../../../../../../../contexts/ScheduleContext";
import { useCalendarAppointments } from "../../../../../../contexts/CalendarAppointmentsContext";
import { useCalendarGroupSessions } from "../../../../../../contexts/CalendarGroupSessionsContext";
import {
  TIME_RANGE_TYPE,
  useCalendarTime,
} from "../../../../../../contexts/CalendarTimeContext";
import { useCalendarTimeline } from "../../contexts/calendar-timeline-context/CalendarTimelineContextProvider";
import useBlockedIntervalsIncludingLiveEdits from "../../hooks/use-blocked-intervals-including-live-edits";
import { getEventsByType } from "../../utils/getEventsByType";
import getIntervalsInRange from "../../utils/getIntervalsInRange";
import styles from "./ScrollableCalendarView.module.scss";
import ScrollableCalendarViewHeader0 from "./components/scrollable-calendar-view-header/ScrollableCalendarViewHeader0";
import ScrollableCalendarViewHeader from "./components/scrollable-calendar-view-header/ScrollableCalendarViewHeader";
import TimeEntry from "./components/time-entry/TimeEntry";
import Timeline from "./components/timeline/Timeline";
import { getScheduleIntervalsByDay } from "./utils/getScheduleIntervalsByDay";
import {
  getNonFullDayEventsWithPosition,
  wrapAppointment,
  wrapBlockedInterval,
  wrapGcalEvent,
  wrapGroupSession,
  WrappedCalendarEvent,
  WrappedCalendarEventWithColumnPosition,
} from "./utils/splitEventsIntoColumns";
import getClasses from "utils/getClasses";

export type TDay = typeof DAYS_OF_WEEK[number];

const HOURS_24 = Array.from({ length: 24 }, (_, i) => i);

const getPlaceholdersForDay = (
  dates: { [key in TDay]: Date },
  placeholders: SessionPlaceholder[],
  timezone: TimeZone,
  disableFullDay: boolean,
) =>
  placeholders.reduce(
    (prev, curr) =>
      getEventsByType<typeof placeholders[number]>({
        dates,
        timezone,
        event: curr,
        start: curr.appointment_start,
        end: curr.appointment_end,
        accum: prev,
        splitBetweenDays: true,
        disableFullDay,
      }),
    { fullDay: {}, nonFullDay: {} } as {
      fullDay: {
        [key in TDay]: typeof placeholders[number][];
      };
      nonFullDay: { [key in TDay]: typeof placeholders[] };
    },
  );

const getEventsForDay = (
  dates: { [key in TDay]: Date },
  appointments: AppointmentMetaData[],
  blockedIntervals: CalendarBlockedInterval[],
  gcalEvents: CalendarBlockedInterval[],
  groupSessions: GroupSessionMetaData[],
  timezone: TimeZone,
) =>
  [
    ...appointments.map(wrapAppointment),
    ...blockedIntervals.map(wrapBlockedInterval),
    ...gcalEvents.map(wrapGcalEvent),
    ...groupSessions.map(wrapGroupSession),
  ].reduce(
    (prev, curr) => {
      return getEventsByType({
        dates,
        timezone,
        event: curr,
        start: curr.start,
        end: curr.end,
        accum: prev,
        splitBetweenDays: true,
      });
    },
    { fullDay: {}, nonFullDay: {} } as {
      fullDay: {
        [key in TDay]: WrappedCalendarEvent[];
      };
      nonFullDay: { [key in TDay]: WrappedCalendarEvent[][] };
    },
  );

type Props = {
  type: Exclude<TIME_RANGE_TYPE, "month">;
  amplitudePageName: AmplitudePageName;
};

const ScrollableCalendarView = ({ type, amplitudePageName }: Props) => {
  const { schedule, busy } = useSchedule();
  const { currentCalendarTimezone, timeRange, onChangeTimeRange } =
    useCalendarTime();

  const { groupSessions } = useCalendarGroupSessions();
  const { appointments, appointmentsInDateRangeError, visibleTeamMemberIds } =
    useCalendarAppointments();
  const { gcalEvents } = useBlockedIntervals();
  const blockedIntervalsIncludingLiveEdits =
    useBlockedIntervalsIncludingLiveEdits();
  const newSessionPlaceholder = useSelector(selectNewSessionPlaceholder);

  const { startOfDay, time } = useCalendarTimeline();

  const datesForWeek = useMemo(() => {
    switch (type) {
      case "day": {
        const momentStartDate = moment.tz(
          timeRange[0],
          currentCalendarTimezone,
        );
        const shortDayName = DAYS_OF_WEEK[momentStartDate.weekday()];
        return {
          [shortDayName]: momentStartDate.toDate(),
        };
      }
      case "threeDays": {
        const dates = {};
        for (let i = 0; i < 3; i++) {
          const dateForDay = moment.tz(timeRange[0], currentCalendarTimezone);
          dateForDay.add(i, "days");
          const shortDayName = DAYS_OF_WEEK[dateForDay.weekday()];
          dates[shortDayName] = dateForDay.toDate();
        }
        return dates as { [k in TDay]: Date };
      }
      default: {
        return DAYS_OF_WEEK.reduce((prev, shortDayName, index) => {
          const dateForDay = moment.tz(timeRange[0], currentCalendarTimezone);
          dateForDay.add(index, "days");
          return {
            ...prev,
            [shortDayName]: dateForDay.toDate(),
          };
        }, {} as { [k in TDay]: Date });
      }
    }
  }, [timeRange, currentCalendarTimezone, type]);

  const scheduleIntervalsByDay = useMemo(() => {
    const calendarStartValue = +timeRange[0];

    const calendarEnd = new Date(calendarStartValue);
    calendarEnd.setDate(calendarEnd.getDate() + 7);
    const calendarEndValue = +calendarEnd;

    const scheduleIntervalsInRange = schedule.reduce(
      (prev, curr) => [
        ...prev,
        ...getIntervalsInRange(
          curr,
          calendarStartValue,
          calendarEndValue,
          currentCalendarTimezone,
        ),
      ],
      [] as ScheduleInterval[],
    );

    return getScheduleIntervalsByDay(
      scheduleIntervalsInRange,
      currentCalendarTimezone,
    );
  }, [schedule, currentCalendarTimezone, timeRange]);

  const eventsWithPosition = useMemo(() => {
    const eventsByDay = getEventsForDay(
      datesForWeek,
      appointments,
      blockedIntervalsIncludingLiveEdits,
      gcalEvents,
      groupSessions,
      currentCalendarTimezone,
    );

    return getNonFullDayEventsWithPosition(eventsByDay);
  }, [
    datesForWeek,
    appointments,
    blockedIntervalsIncludingLiveEdits,
    gcalEvents,
    groupSessions,
    currentCalendarTimezone,
  ]);

  const eventsByTeamMember = useMemo(() => {
    if (visibleTeamMemberIds.length <= 1) return {};

    const result: Record<string, typeof eventsWithPosition> = {};
    visibleTeamMemberIds.forEach((teamMemberId) => {
      result[teamMemberId] = {};
      Object.entries(eventsWithPosition).forEach(([day, dayEvents]) => {
        result[teamMemberId][day] = dayEvents.map((hourEvents) =>
          hourEvents.filter((event) => {
            if (event.type === "appointment") {
              return event.event.orchid_pro.cognito_sub === teamMemberId;
            }
            return false;
          }),
        );
      });
    });
    return result;
  }, [eventsWithPosition, visibleTeamMemberIds]);

  const placeholdersByDay = useMemo(
    () =>
      getPlaceholdersForDay(
        datesForWeek,
        newSessionPlaceholder ? [newSessionPlaceholder] : [],
        currentCalendarTimezone,
        true,
      ),
    [datesForWeek, newSessionPlaceholder, currentCalendarTimezone],
  );

  const containerRef = useRef<HTMLTableElement | null>(null);
  const defaultStartOfDayElementRef = useRef<HTMLTableRowElement | null>(null);
  const scrollableContentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    defaultStartOfDayElementRef.current?.scrollIntoView(true);

    // Scroll to today in multi-calendar week view
    if (visibleTeamMemberIds.length > 1 && type === "week") {
      setTimeout(() => {
        const container = scrollableContentRef.current;
        if (!container) return;

        const currentMoment = moment().tz(currentCalendarTimezone);
        // Use weekday() which returns 0-6 (Sunday=0, Monday=1, etc.)
        // This matches the index in DAYS_OF_WEEK array
        const dayIndex = currentMoment.weekday();

        if (dayIndex < 0 || dayIndex >= DAYS_OF_WEEK.length) {
          return;
        }

        const dayWidth = container.scrollWidth / 7;
        // Scroll to the beginning of the current day (so it appears at the left edge)
        const scrollLeft = Math.max(0, dayIndex * dayWidth);

        container.scrollTo({ left: scrollLeft, behavior: "smooth" });
      }, 500);
    }
  }, [currentCalendarTimezone, visibleTeamMemberIds.length, type]);

  const handlers = useSwipeable({
    onSwipedLeft: () => onChangeTimeRange(1),
    onSwipedRight: () => onChangeTimeRange(-1),
    delta: 40,
    swipeDuration: 500,
  });

  const renderTimeEntry = ({
    day,
    hourIndex,
    teamMemberId,
    teamMemberEvents,
  }: {
    day: string;
    hourIndex: number;
    teamMemberId?: string;
    teamMemberEvents?: WrappedCalendarEventWithColumnPosition[];
  }) => {
    const isToday = startOfDay.isSame(moment(datesForWeek[day]), "days");

    return (
      <TimeEntry
        key={`${day}-${hourIndex}${teamMemberId ? `-${teamMemberId}` : ""}`}
        className={styles["table__td"]}
        date={datesForWeek[day]}
        isToday={isToday}
        children={
          isToday &&
          time.hour === hourIndex && <Timeline minutes={time.minutes} />
        }
        hour={hourIndex}
        eventsWithPosition={
          teamMemberEvents || eventsWithPosition[day]?.[hourIndex]
        }
        scheduleIntervals={
          busy ? [] : (scheduleIntervalsByDay[day] || [])[hourIndex]
        }
        placeholders={
          busy
            ? []
            : (() => {
                // In multi-calendar week view, only show placeholder for the specific team member
                if (visibleTeamMemberIds.length > 1 && type === "week") {
                  const isCorrectTeamMember =
                    teamMemberId === newSessionPlaceholder?.team_member_id;
                  return isCorrectTeamMember
                    ? placeholdersByDay.nonFullDay[day]?.[hourIndex] || []
                    : [];
                }
                // Single calendar view - show placeholder for all cells
                return placeholdersByDay.nonFullDay[day]?.[hourIndex] || [];
              })()
        }
        amplitudePageName={amplitudePageName}
        teamMemberId={teamMemberId}
      />
    );
  };

  const renderErrorTag = () =>
    appointmentsInDateRangeError && (
      <ErrorTag
        show={true}
        onHide={() => null}
        duration={3000}
        errorMsg={appointmentsInDateRangeError}
      />
    );

  const renderHourLabel = (hour: number) => (
    <th scope="row" className={styles["hour-label-container"]}>
      {hour !== 0 ? (
        <h6 className={styles["table__hour-label"]}>
          {`${hour === 0 ? 12 : hour > 12 ? hour - 12 : hour} ${
            hour < 12 ? "AM" : "PM"
          }`}
        </h6>
      ) : null}
      <div
        ref={hour === 8 ? defaultStartOfDayElementRef : null}
        className={styles["table__hour-label-divider"]}
      />
    </th>
  );

  if (visibleTeamMemberIds.length > 1 && type === "week") {
    return (
      <>
        {renderErrorTag()}
        <div className={styles["calendar-layout"]}>
          <div
            className={styles["scrollable-content"]}
            {...handlers}
            ref={scrollableContentRef}
          >
            <table
              className={getClasses(
                styles["table--team-members-visible"],
                styles["table"],
              )}
              ref={containerRef}
            >
              <thead>
                <ScrollableCalendarViewHeader0
                  datesForWeek={datesForWeek}
                  today={startOfDay.toDate()}
                />
              </thead>
              <tbody>
                {HOURS_24.map((hour, hourIndex) => (
                  <tr key={hour}>
                    {renderHourLabel(hour)}
                    {Object.entries(datesForWeek).map(([day, date]) => {
                      if (busy) {
                        return (
                          <td
                            key={`${day}-${hourIndex}`}
                            className={styles["table__td"]}
                          />
                        );
                      }

                      return visibleTeamMemberIds.map((teamMemberId) => {
                        const teamMemberEvents =
                          eventsByTeamMember[teamMemberId]?.[day]?.[
                            hourIndex
                          ] || [];
                        return renderTimeEntry({
                          day,
                          hourIndex,
                          teamMemberId,
                          teamMemberEvents,
                        });
                      });
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {renderErrorTag()}
      <ScrollableCalendarViewHeader
        datesForWeek={datesForWeek}
        today={startOfDay.toDate()}
      />
      <div className={styles["container"]} {...handlers}>
        <table className={styles["table"]} ref={containerRef}>
          <tbody>
            {HOURS_24.map((hour, hourIndex) => (
              <tr key={hour}>
                {renderHourLabel(hour)}
                {Object.entries(datesForWeek).map(([day, date]) => {
                  if (busy) {
                    return (
                      <td
                        key={`${day}-${hourIndex}`}
                        className={styles["table__td"]}
                      />
                    );
                  }
                  return renderTimeEntry({ day, hourIndex });
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default ScrollableCalendarView;
